import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import './accounting.css';

const CashRegisters = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [cashRegisters, setCashRegisters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statistics, setStatistics] = useState(null);

  useEffect(() => {
    fetchCashRegisters();
    fetchStatistics();
  }, []);

  const fetchCashRegisters = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://naql.nozzm.com/api/accounting/cash-registers', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setCashRegisters(data.data || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch('https://naql.nozzm.com/api/accounting/cash-registers/statistics', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleToggleStatus = async (registerId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/accounting/cash-registers/${registerId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      fetchCashRegisters();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'حدث خطأ في تغيير الحالة');
    }
  };

  const handleSetDefault = async (registerId) => {
    try {
      const response = await fetch(`https://naql.nozzm.com/api/accounting/cash-registers/${registerId}/set-default`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'lang': i18n.language,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      fetchCashRegisters();
      fetchStatistics();
    } catch (err) {
      setError(err.message || 'حدث خطأ في تعيين الخزنة الافتراضية');
    }
  };

  const handleDelete = async (registerId) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الخزنة؟')) {
      try {
        const response = await fetch(`https://naql.nozzm.com/api/accounting/cash-registers/${registerId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'lang': i18n.language,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Network response was not ok');
        }

        fetchCashRegisters();
        fetchStatistics();
      } catch (err) {
        setError(err.message || 'حدث خطأ في حذف الخزنة');
      }
    }
  };

  const filteredCashRegisters = cashRegisters.filter(register => {
    const matchesSearch = register.register_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         register.register_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || register.status === statusFilter;
    const matchesType = !typeFilter || register.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    const statusClasses = {
      'active': 'bg-success',
      'inactive': 'bg-secondary',
      'closed': 'bg-danger',
      'suspended': 'bg-warning'
    };
    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {t(`accounting.cashRegisters.registerStatus.${status}`)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    return (
      <span className="badge bg-info">
        {t(`accounting.cashRegisters.registerTypes.${type}`)}
      </span>
    );
  };

  const getBalanceStatus = (currentBalance, minBalance, maxBalance) => {
    if (currentBalance < minBalance) {
      return <span className="badge bg-danger">منخفض</span>;
    } else if (currentBalance > maxBalance) {
      return <span className="badge bg-warning">مرتفع</span>;
    } else {
      return <span className="badge bg-success">طبيعي</span>;
    }
  };

  if (loading) {
    return (
      <div className="cash-registers-container">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cash-registers-container">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="cash-registers-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>{t('accounting.cashRegisters.title')}</h2>
          <p>إدارة الخزائن النقدية</p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/cash-registers/add')}
          >
            {t('accounting.cashRegisters.actions.add')}
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="statistics-cards">
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <h3>{statistics?.total_registers || 0}</h3>
            <p>{t('accounting.cashRegisters.statistics.totalRegisters')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{statistics?.active_registers || 0}</h3>
            <p>{t('accounting.cashRegisters.statistics.activeRegisters')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💵</div>
          <div className="stat-content">
            <h3>{statistics?.total_balance ? `${statistics.total_balance.toLocaleString()} ريال` : '0 ريال'}</h3>
            <p>{t('accounting.cashRegisters.statistics.totalBalance')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <h3>{statistics?.default_register ? '1' : '0'}</h3>
            <p>{t('accounting.cashRegisters.statistics.defaultRegister')}</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="row">
          <div className="col-md-4">
            <div className="search-box">
              <input
                type="text"
                className="form-control"
                placeholder="البحث في الخزائن..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">جميع الحالات</option>
              <option value="active">نشط</option>
              <option value="inactive">غير نشط</option>
              <option value="closed">مغلق</option>
              <option value="suspended">معلق</option>
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <option value="">جميع الأنواع</option>
              <option value="main">خزنة رئيسية</option>
              <option value="branch">خزنة فرع</option>
              <option value="temporary">خزنة مؤقتة</option>
            </select>
          </div>
          <div className="col-md-2">
            <button 
              className="btn btn-outline-secondary w-100"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setTypeFilter('');
              }}
            >
              مسح الفلاتر
            </button>
          </div>
        </div>
      </div>

      {/* Cash Registers Table */}
      <div className="table-container">
        <div className="table-responsive">
          <table className="table table-hover">
            <thead>
              <tr>
                <th>{t('accounting.cashRegisters.registerCode')}</th>
                <th>{t('accounting.cashRegisters.registerName')}</th>
                <th>{t('accounting.cashRegisters.type')}</th>
                <th>{t('accounting.cashRegisters.location')}</th>
                <th>{t('accounting.cashRegisters.currentBalance')}</th>
                <th>حالة الرصيد</th>
                <th>{t('accounting.cashRegisters.status')}</th>
                <th>{t('accounting.cashRegisters.actions.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCashRegisters.map((register) => (
                <tr key={register.id}>
                  <td>{register.register_code}</td>
                  <td>{register.register_name}</td>
                  <td>{getTypeBadge(register.type)}</td>
                  <td>{register.location || '-'}</td>
                  <td>{register.current_balance ? `${register.current_balance.toLocaleString()} ريال` : '0 ريال'}</td>
                  <td>{getBalanceStatus(register.current_balance, register.min_balance, register.max_balance)}</td>
                  <td>{getStatusBadge(register.status)}</td>
                  <td>
                    <div className="btn-group" role="group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/cash-registers/view/${register.id}`)}
                        title={t('accounting.cashRegisters.actions.view')}
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-warning"
                        onClick={() => navigate(`/cash-registers/edit/${register.id}`)}
                        title={t('accounting.cashRegisters.actions.edit')}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => handleToggleStatus(register.id)}
                        title={t('accounting.cashRegisters.actions.toggleStatus')}
                      >
                        <i className={`fas fa-${register.status === 'active' ? 'pause' : 'play'}`}></i>
                      </button>
                      {!register.is_default && (
                        <button
                          className="btn btn-sm btn-outline-info"
                          onClick={() => handleSetDefault(register.id)}
                          title={t('accounting.cashRegisters.actions.setDefault')}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(register.id)}
                        title={t('accounting.cashRegisters.actions.delete')}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCashRegisters.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted">لا توجد خزائن لعرضها</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CashRegisters;
